import { inject, Injectable } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CurrentStateService } from '@ddv/behaviors';
import { ApiServices, ApiExecutorService } from '@ddv/http';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DdvDate } from '@ddv/models';
import { map, mergeMap, Observable, shareReplay } from 'rxjs';

import { FundPeriodsInfo } from './fund-periods-info';
import { HiDate } from './hi-date';

@Injectable()
export class HiDatesService {
    private readonly currentState = inject(CurrentStateService);
    private readonly trebekApiExecutor = inject<ApiExecutorService>(ApiServices.trebek);

    private hiDates$: Observable<HiDate[]> | undefined;

    private get hiDatesEndpoint(): Observable<{ clientCode: string, route: string }> {
        return this.currentState.clientCode$
            .pipe(map((clientCode) => {
                return {
                    clientCode,
                    route: 'investors/fundsPeriodsInfo',
                };
            }));
    }

    hiDates(): Observable<HiDate[]> {
        this.hiDates$ ??= this.getFundsPeriodsInfo()
            .pipe(map((raw) => this.transformToHiDates(raw)))
            .pipe(shareReplay(1)); // replays only most recent

        return this.hiDates$;
    }

    private getFundsPeriodsInfo(): Observable<FundPeriodsInfo[]> {
        return this.hiDatesEndpoint
            .pipe(mergeMap(({ clientCode, route }) => {
                return this.trebekApiExecutor.invokeServiceWithBody<FundPeriodsInfo[]>(clientCode, route, 'POST', { includeFunds: [] });
            }));
    }

    private transformToHiDates(fundsPeriodsInfo: FundPeriodsInfo[]): HiDate[] {
        const dates = new Map<string, number>(); // key is the date, value is the number of the funds posted
        let numFunds = 0;

        fundsPeriodsInfo.forEach((fpi) => {
            numFunds += 1;
            fpi.periodsInfo.forEach((pi) => {
                if (!dates.has(pi.date)) {
                    dates.set(pi.date, 0);
                }

                if (pi.posted) {
                    dates.set(pi.date, (dates.get(pi.date) ?? 0) + 1);
                }
            });
        });

        const result: HiDate[] = [];
        dates.forEach((numFundsPosted, date) => {
            result.push(new HiDate({
                date: DdvDate.fromUSFormat(date),
                numPosted: numFundsPosted,
                numTotal: numFunds,
            }));
        });
        result.sort((a, b) => b.date.toMilliseconds() - a.date.toMilliseconds());

        return result;
    }
}
